import React from 'react';
import { Card, Button } from '@parsina/framework';
import { ChevronDown } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { readJson, writeJson, RECORD_SECTIONS_KEY } from '../../lib/drafts.js';

// One collapsible block of a patient record — the شرح حال sections on the
// record's history tab, and the read-only rail inside a visit (core.md §7.4,
// §7.5).
//
// ── The open/closed state is REMEMBERED, per section, per browser ────────
//
// A record has eight or nine of these and a student reads two of them. Without
// persistence every navigation back into the record re-collapses (or re-opens)
// everything, and the student re-performs the same four clicks each time. The
// state lives in ONE localStorage object (`record_sections_v1`) keyed by
// section id rather than one key per section, so clearing it is one delete and
// a section added later simply has no entry yet — which is why `defaultOpen`
// still exists.
//
// Storage is wrapped in try/catch by src/lib/drafts.js and the component
// renders correctly with nothing stored: a private window must not be a broken
// record.
//
// ── `locked` is for the allergy alert and nothing else ───────────────────
//
// «هشدار حساسیت» renders first and cannot be collapsed (core.md §7.4/§7.5). A
// safety notice that a student can fold away is one they fold away once and
// never see again — the same reasoning that makes `AiDisclaimer`
// non-dismissable.

const readOpenMap = () => readJson(RECORD_SECTIONS_KEY, { kind: 'local', fallback: {} }) || {};

/** Is this section open right now? Exported for the tests and the rail. */
export function isSectionOpen(id, defaultOpen = true) {
  const stored = readOpenMap()[id];
  return typeof stored === 'boolean' ? stored : defaultOpen;
}

export default function RecordSection({
  id,
  title,
  icon: Icon,
  count,
  action,
  locked = false,
  danger = false,
  defaultOpen = true,
  children,
  'data-testid': testId,
}) {
  const { formatNumber } = useTranslation();
  const [open, setOpen] = React.useState(() => (locked ? true : isSectionOpen(id, defaultOpen)));
  const bodyId = `record-section-${id}`;

  const toggle = () => {
    if (locked) return;
    setOpen((prev) => {
      const next = !prev;
      writeJson(RECORD_SECTIONS_KEY, { ...readOpenMap(), [id]: next }, { kind: 'local' });
      return next;
    });
  };

  return (
    <Card
      data-testid={testId || `record-section-${id}`}
      style={{
        padding: 0,
        overflow: 'hidden',
        ...(danger ? { borderColor: C.red, background: CT.bg } : null),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
        }}
      >
        {/* The heading IS the toggle, but only when there is something to
            toggle: a locked section renders the same row as plain text so a
            keyboard user is not offered a button that does nothing. */}
        {locked ? (
          <span style={HEAD_STYLE(danger)}>
            {Icon && <Icon size={16} color={danger ? C.red : C.teal} aria-hidden />}
            {title}
          </span>
        ) : (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={bodyId}
            style={{
              ...HEAD_STYLE(danger),
              background: 'none',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
            }}
            data-testid={`record-section-toggle-${id}`}
          >
            <ChevronDown
              size={15}
              aria-hidden
              style={{
                transition: 'transform .15s',
                transform: open ? 'none' : 'rotate(-90deg)',
                color: CT.mute,
              }}
            />
            {Icon && <Icon size={16} color={danger ? C.red : C.teal} aria-hidden />}
            {title}
          </button>
        )}

        {count != null && count !== '' && (
          <span style={{ fontSize: 11, fontWeight: 700, color: CT.mute }}>
            {typeof count === 'number' ? formatNumber(count) : count}
          </span>
        )}

        {action && <span style={{ marginInlineStart: 'auto' }}>{action}</span>}
      </div>

      {open && (
        <div id={bodyId} style={{ padding: '0 12px 12px' }}>
          {children}
        </div>
      )}
    </Card>
  );
}

const HEAD_STYLE = (danger) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 13.5,
  fontWeight: 800,
  color: danger ? C.red : C.textPrimary,
  textAlign: 'start',
});

/**
 * The «مشاهدهٔ همه» affordance a section's header carries when its body is
 * showing a clipped list. Rendered as the section's `action`.
 */
export function ViewAllButton({ onClick, testId }) {
  const { t } = useTranslation();
  return (
    <Button variant="ghost" size="sm" onClick={onClick} data-testid={testId}>
      {t('common.actions.viewAll')}
    </Button>
  );
}
